import type {
  AcceptAllResponseDto,
  AuthorMappingDetailDto,
  AuthorMappingListDto,
  AuthorSyncStatusDto,
  IdentitySource,
  MappingDecision,
  MappingDecisionResponseDto,
  MappingSuggestionDecisionResponseDto,
  MappingSuggestionListDto,
  StartSyncResponseDto,
} from "@shelflist/shared";
import {
  MAPPING_PAGE_SIZE_DEFAULT,
  MAPPING_STATUSES,
  MAPPING_SUGGESTION_MIN_SCORE,
  SYNC_POLL_ACTIVE_MS,
  SYNC_POLL_IDLE_MS,
} from "@shelflist/shared";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { adminKeys } from "./admin";
import { api, call } from "./api";

export const mappingKeys = {
  all: ["admin", "author-mapping"] as const,
  list: (params: MappingSearch) => ["admin", "author-mapping", "list", params] as const,
  suggestions: (params: MappingSuggestionsSearch) =>
    ["admin", "author-mapping", "suggestions", params] as const,
  detail: (authorId: string) => ["admin", "author-mapping", "detail", authorId] as const,
  sync: ["admin", "author-mapping", "sync"] as const,
};

/** Search params of the mapping list: status filter, name filter and page live in the URL. */
export const mappingSearchSchema = z.object({
  status: z.enum(MAPPING_STATUSES).optional().catch(undefined),
  q: z.string().max(100).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
export type MappingSearch = z.infer<typeof mappingSearchSchema>;

/** Search params of the "Suggestions" view of the mapping page. */
export const mappingSuggestionsSearchSchema = z.object({
  source: z.enum(["openlibrary", "dnb"]).optional().catch(undefined),
  minConfidence: z.coerce
    .number()
    .int()
    .min(MAPPING_SUGGESTION_MIN_SCORE)
    .max(100)
    .optional()
    .catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});
export type MappingSuggestionsSearch = z.infer<typeof mappingSuggestionsSearchSchema>;

export const mappingListQuery = (params: MappingSearch) =>
  queryOptions({
    queryKey: mappingKeys.list(params),
    queryFn: (): Promise<AuthorMappingListDto> =>
      call(
        api.admin["author-mapping"].$get({
          query: {
            page: String(params.page ?? 1),
            pageSize: String(MAPPING_PAGE_SIZE_DEFAULT),
            ...(params.status ? { status: params.status } : {}),
            ...(params.q ? { q: params.q } : {}),
          },
        }),
      ),
  });

/** Poll every 5 s while a sync is queued or running or the sweep runs, else every 60 s. */
export function syncRefetchInterval(data: AuthorSyncStatusDto | undefined): number {
  return data && (data.active || data.sweep.running) ? SYNC_POLL_ACTIVE_MS : SYNC_POLL_IDLE_MS;
}

/** Status of the author sync (what needs it, the running or last run, the sweep). */
export const syncStatusQuery = queryOptions({
  queryKey: mappingKeys.sync,
  queryFn: (): Promise<AuthorSyncStatusDto> =>
    call(api.admin["author-mapping"].sync.$get({ query: {} })),
  refetchInterval: (query) => syncRefetchInterval(query.state.data),
});

export const mappingSuggestionsQuery = (params: MappingSuggestionsSearch) =>
  queryOptions({
    queryKey: mappingKeys.suggestions(params),
    queryFn: (): Promise<MappingSuggestionListDto> =>
      call(
        api.admin["author-mapping"].suggestions.$get({
          query: {
            page: String(params.page ?? 1),
            pageSize: String(MAPPING_PAGE_SIZE_DEFAULT),
            ...(params.source ? { source: params.source } : {}),
            ...(params.minConfidence ? { minConfidence: String(params.minConfidence) } : {}),
          },
        }),
      ),
  });

export const mappingDetailQuery = (authorId: string) =>
  queryOptions({
    queryKey: mappingKeys.detail(authorId),
    queryFn: (): Promise<AuthorMappingDetailDto> =>
      call(api.admin["author-mapping"][":authorId"].$get({ param: { authorId } })),
  });

/** Every mapping change can alter the list, the suggestions, the detail and the overview card. */
function useInvalidate() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: mappingKeys.all }),
      queryClient.invalidateQueries({ queryKey: adminKeys.overview }),
    ]);
}

export function useDecideMapping(authorId: string, source: IdentitySource) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (decision: MappingDecision): Promise<MappingDecisionResponseDto> =>
      call(
        api.admin["author-mapping"][":authorId"][":source"].$put({
          param: { authorId, source },
          json: decision,
        }),
      ),
    meta: { handlesErrors: true },
    onSuccess: invalidate,
  });
}

/**
 * Starts the author sync (`all: true` re-checks authors that were checked recently, too). The
 * response has `jobId: null` and `authors: 0` when nothing needs a sync.
 */
export function useStartSync() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: { all: boolean }): Promise<StartSyncResponseDto> =>
      call(api.admin["author-mapping"].sync.$post({ json: input })),
    meta: { handlesErrors: true },
    onSuccess: invalidate,
  });
}

/** Accepts or rejects a suggested candidate (`externalId` omitted: the best one). */
export function useSuggestionAction(action: "accept" | "reject") {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: {
      authorId: string;
      source: IdentitySource;
      externalId?: string;
    }): Promise<MappingSuggestionDecisionResponseDto> =>
      call(
        api.admin["author-mapping"][":authorId"][":source"][action].$post({
          param: { authorId: input.authorId, source: input.source },
          json: input.externalId ? { externalId: input.externalId } : {},
        }),
      ),
    meta: { handlesErrors: true },
    onSuccess: invalidate,
  });
}

export function useAcceptAll() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (minConfidence: number): Promise<AcceptAllResponseDto> =>
      call(
        api.admin["author-mapping"].suggestions["accept-all"].$post({ json: { minConfidence } }),
      ),
    meta: { handlesErrors: true },
    onSuccess: invalidate,
  });
}
